import { path } from 'ramda'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import ContentWrapper from '@/components/content-wrapper'
import { confirm } from '@/components/dialog'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import useFinancialFiles from '@/hooks/use-financial-files'
import { AutomobileList } from '@/features/Profile/Automobile'
import { EmergencyContactsList } from '@/features/Profile/EmergencyContacts'
import { EmploymentInfo } from '@/features/Profile/EmploymentInfo'
import { EmploymentReferencesList } from '@/features/Profile/EmploymentReferences'
import { InsuranceView } from '@/features/Profile/InsuranceInfo'
import { LandlordReferencesList } from '@/features/Profile/LandlordReferences'
import LivScore from '@/features/Profile/LivScore'
import { PetsList } from '@/features/Profile/Pets'
import { RoommatesList } from '@/features/Profile/Roommates'
import TenantBasicInfo from '@/features/Profile/TenantBasicInfo'
import useRole from '@/hooks/use-role'
import useCurrentSession from '@/hooks/use-current-session'
import useBetaUser from '@/hooks/use-beta-user'
import { select } from '@/store'
import { breakpoints, fontWeights } from '@/styles'
import { toast } from '@/components/toast-notifications'
import useScrollToTop from '@/hooks/use-scroll-to-top'
import AreaTitle from '@/components/area-title'
import Highlight from './tour'
import InfoView from './info-view'
import { show as showExistingContract } from './existing-contract-modal'

const StyledContentWrapper = styled(ContentWrapper)`
  position: relative;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: none;
  }
`
const Inner = styled.div`
  > ${AreaTitle} + div {
    > div:first-of-type {
      height: 48px;
    }
  }
`
const StyledAreaTitle = styled(AreaTitle)``
const SubTitle = styled(AreaTitle)`
  margin: 72px 0 ${props => (props.noPanel ? '32' : '0')}px;
`
const Wrapper = styled(Page)`
  @media print {
    > div {
      display: flex;
      flex-direction: column;
      max-width: 900px;
    }

    h2 {
      font-size: 24px;
      font-weight: ${fontWeights.medium};
    }
  }
`

const BaseButton = ({ onClick, to, data, ...rest }) => {
  const extra =
    typeof onClick == 'function'
      ? { onClick }
      : {
          as: NavLink,
          to: {
            pathname: to,
            state: {
              scrollToTop: true,
              ...data
            }
          }
        }
  return <Button theme="primary" size="small" left={10} {...rest} {...extra} />
}

export const ApplicationView = ({
  match: {
    params: { applicationId, listingId, timelineId }
  },
  history
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userRole = useRole()

  const session = useCurrentSession()

  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const betaUser = useBetaUser()
  const betaUserContractLite = betaUser.custom_contract
  const currentBetaUser = betaUserContractLite.includes(parseInt(session.id))

  useScrollToTop(true)

  const application = useSelector(state =>
    select.application.applicationDetail(state, {
      applicationId
    })
  )

  useEffect(() => {
    if (isDataLoaded) return
    const loadApplication = async () => {
      await dispatch.application.load(applicationId)
      setIsDataLoaded(true)
    }
    loadApplication()
  }, [applicationId, dispatch.application, isDataLoaded])

  const secureFiles = useFinancialFiles(application?.user_info?.files.user_secure_files, userRole)

  const applicationListingId = listingId || application?.listing_id
  useEffect(() => {
    if (applicationListingId) {
      dispatch.listing.load(applicationListingId)
    }
  }, [dispatch.listing, applicationListingId])

  const listingContract = useSelector(path(['listing', application?.listing_id, 'contract']))

  useEffect(() => {
    if (application?.listing_id && userRole === 'landlord') {
      dispatch.listing.getContract(application.listing_id)
    }
  }, [application?.listing_id, dispatch.listing, userRole])

  const hasContract =
    listingContract?.listing_contract_id && listingContract?.state_machine !== 'draft'

  const sentContract = listingContract?.state_machine === 'sent'

  const listing = useSelector(path(['listing', applicationListingId, 'listings']))
  const markedAsRented = listing?.marked_as_rented === '1'

  const hasValidContract =
    hasContract && listingContract?.tenant_ids?.includes(application?.user_info?.id)

  const confirmWithdraw = () => {
    return confirm(
      t('l.withdraw_application_modal.edit.withdraw_application.title', 'Withdraw Application?'),
      t(
        'l.withdraw_application_modal.edit.confirmation.text',
        'Are you sure you want to withdraw this application?'
      )
    ).then(async () => {
      const { error } = await dispatch.application.delete(application.id)
      if (!error) {
        history.push(`/chat/${application.timeline_room}`)
        dispatch.chat.updateApplications(application.timeline_room)
      }
    })
  }

  const applicationAvailable = !!application.id && !!application.listing_info

  const hideContract = !applicationAvailable || sentContract

  const showTourHighlight =
    !(userRole !== 'landlord' || hideContract || markedAsRented) &&
    (!hasContract || !hasValidContract)

  const ViewContract = () => {
    if (userRole !== 'landlord' || hideContract || markedAsRented) return null
    const create = t('l.application.view.create_contract.button', 'Create Contract')
    if (!hasContract) {
      const link = currentBetaUser
        ? `/listing/${applicationListingId}/contract/choose/types`
        : `/application/${applicationId}/contract/create`
      return (
        <BaseButton
          id="create-contract-btn"
          to={link}
          data={{ applicantId: application.user_info?.id, applicationId }}
        >
          {create}
        </BaseButton>
      )
    }
    if (!hasValidContract)
      return (
        <BaseButton id="create-contract-btn" onClick={showExistingContract}>
          {create}
        </BaseButton>
      )
    return (
      <BaseButton to={`/contract/${listingContract.listing_contract_id}`}>
        {t('l.application.view.view_contract.button', 'View Contract')}
      </BaseButton>
    )
  }

  const backUrl = listingId ? `/listings/${listingId}/applications` : `/chat/${timelineId}`

  useEffect(() => {
    if (!isDataLoaded || applicationAvailable) return
    toast(
      t(
        'b.application.view.tenancy_application_not_available.text',
        'This application is no longer available.'
      ),
      {
        title: t(
          'b.application.view.tenancy_application_not_available.title',
          'Application Withdrawn'
        ),
        type: 'warning'
      }
    )
  }, [applicationAvailable, isDataLoaded, t])

  if (!applicationAvailable && isDataLoaded) return <Redirect to={backUrl} />

  return (
    <Wrapper>
      <StyledContentWrapper className="mb-50">
        <Helmet>
          <title>{t('b.application.view.tenancy_application.title', 'Tenancy Application')}</title>
        </Helmet>
        <PageTitle>
          {t('b.application.view.tenancy_application.title', 'Tenancy Application')}
        </PageTitle>
        <StyledAreaTitle>
          {t('b.application.view.tenancy_application.title', 'Tenancy Application')}
        </StyledAreaTitle>
        <Inner>
          <InfoView application={application} />
          <SubTitle>
            {t('b.application.view.tenancy_application.basic_info.title', 'Basic Info')}
          </SubTitle>
          <TenantBasicInfo data={application.user_info} />
          <PetsList
            data={application.user_pets}
            emptyMessage={t('r.application.view.empty_pets.text', 'No pets in this application')}
          />
          <EmergencyContactsList
            data={application.emergency_contacts}
            emptyMessage={t(
              'r.application.view.empty_emergency_contacts.text',
              'No emergency contacts in this application'
            )}
          />
          <SubTitle noPanel>
            {t('b.application.view.tenancy_application.liv_score.title', 'Trust Score')}
          </SubTitle>
          {userRole === 'tenant' && (
            <LivScore
              hideTitle
              userRole={userRole}
              livScore={application.liv_score}
              data={application.user_info}
              listingData={application.listing_info?.listings?.[application.listing_id]}
            />
          )}

          <EmploymentInfo data={application.user_info} secureFiles={secureFiles} />
          <SubTitle>
            {t('b.application.view.tenancy_application.references.title', 'References')}
          </SubTitle>
          <EmploymentReferencesList
            data={application.user_employers}
            emptyMessage={t(
              'r.application.view.empty_employment_references.text',
              'No employment references in this application'
            )}
          />
          <LandlordReferencesList
            data={application.landlord_references}
            emptyMessage={t(
              'r.application.view.empty_landlord_references.text',
              'I am a first-time renter and do not have any landlord references'
            )}
          />
          <SubTitle>
            {t('b.application.view.tenancy_application.extra_info.title', 'Extra Info')}
          </SubTitle>
          <RoommatesList
            data={application.user_roommates}
            emptyMessage={t(
              'r.application.view.empty_roommates.text',
              'No roommates in this application'
            )}
          />
          <InsuranceView insurance={application.insurance} />
          <AutomobileList
            data={application.user_vehicles}
            emptyMessage={t('r.profile.view.no_automobiles.text', 'No automobiles added')}
          />
        </Inner>
      </StyledContentWrapper>
      <DefaultButtonBar>
        {userRole === 'tenant' && applicationAvailable && !markedAsRented && (
          <Button theme="delete" size="small" onClick={confirmWithdraw}>
            {t('l.button_bar.edit.withdraw.button', 'Withdraw')}
          </Button>
        )}
        <ViewContract />
      </DefaultButtonBar>
      {showTourHighlight && <Highlight />}
    </Wrapper>
  )
}

export default ApplicationView
